// @ts-nocheck
import React, { useEffect, useId } from 'react';
import { Copy, XCircle } from 'lucide-react';

export type UserInviteCredentials = {
  email: string;
  temporaryPassword: string;
  passwordResetLink: string;
};

export type UserInviteCredentialsDialogProps = {
  open: boolean;
  title: string;
  lede: string;
  data: UserInviteCredentials | null;
  onClose: () => void;
};

async function copyText(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

/**
 * One-time invite: temporary password + reset link (aligned with perk-loyalty-app onboarding).
 */
export default function UserInviteCredentialsDialog({
  open,
  title,
  lede,
  data,
  onClose,
}: UserInviteCredentialsDialogProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !data) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-lg rounded-xl bg-white shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto"
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 pr-2">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg shrink-0"
            aria-label="Close"
          >
            <XCircle size={20} aria-hidden />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">{lede}</p>
          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium text-gray-500 uppercase">Email</span>
                <button
                  type="button"
                  onClick={() => void copyText(data.email)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
                >
                  <Copy size={14} aria-hidden />
                  Copy
                </button>
              </div>
              <code className="text-sm text-gray-900 break-all">{data.email}</code>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium text-gray-500 uppercase">Temporary password</span>
                <button
                  type="button"
                  onClick={() => void copyText(data.temporaryPassword)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
                >
                  <Copy size={14} aria-hidden />
                  Copy
                </button>
              </div>
              <code className="text-sm text-gray-900 break-all">{data.temporaryPassword}</code>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium text-gray-500 uppercase">Reset link</span>
                <button
                  type="button"
                  onClick={() => void copyText(data.passwordResetLink)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
                >
                  <Copy size={14} aria-hidden />
                  Copy
                </button>
              </div>
              <p className="text-sm text-gray-800 break-all font-mono">{data.passwordResetLink}</p>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg btn-primary text-sm font-medium">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
